/* eslint-disable camelcase */
import * as eff from 'redux-saga/effects';
import createActionCreators from 'redux-resource-action-creators';
import { DateTime } from 'luxon';

import {
  actionTypes,
  setProfileState,
} from '../actions';
import {
  getProfile,
} from '../selectors';
import api from '../api';

import {
  getIntentTask,
  watchCheckLibrary,
} from './utils';
import {
  throwError,
} from './helpers';

export default function* authFlow(reportChannel) {
  function* login({
    payload,
    redirectTo,
    updateProfile,
    callbacks,
  }) {
    try {
      const {
        user,
        access_token: accessToken,
      } = yield eff.call(
        api.authPassword,
        {
          body: {
            ...payload,
            grant_type: 'password',
          },
        },
      );

      yield eff.call(api.setAccessToken, accessToken);
      yield eff.call(api.setAdditionalParams, { userId: user.id });
      yield eff.put(
        reportChannel,
        {
          messageType: 'loginSuccess',
          message: {
            user,
            redirectTo,
            accessToken,
          },
        },
      );

      // TODO api bug
      if (user.is_pro && updateProfile) {
        yield eff.call(
          api.updateEntity,
          {
            type: 'user_profiles',
            id: user.user_profile.id,
            body: updateProfile,
          },
        );
      }

      callbacks?.setSubmitting?.(false);
      callbacks?.onSuccess?.(user);

      yield eff.put({
        type: 'GC_ANALYTICS',
        eventKey: user.is_pro ? 'pro_login' : 'client_login',
      });
    } catch (e) {
      callbacks?.setSubmitting?.(false);
      callbacks?.onError?.(e);

      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'error',
            body: 'Invalid Email or Password.',
          },
        },
      );

      yield eff.call(throwError, e);
    }
  }

  function* signUp({
    payload,
    redirectTo,
    callbacks,
  }) {
    yield eff.put({
      type: 'GC_ANALYTICS',
      eventKey: 'signup_submit',
      attrs: payload,
    });

    try {
      const res = yield eff.call(
        payload.invitation_token ? api.invitation : api.createUser,
        {
          body: {
            user: payload,
          },
        },
      );

      if (payload.is_pro) {
        yield eff.put({
          type: 'GC_ANALYTICS',
          eventKey: 'pro_signup',
          attrs: {
            ...payload,
            id: res.id,
          },
        });
      } else {
        yield eff.put({
          type: 'GC_ANALYTICS',
          eventKey: 'client_accepted_invitation',
          attrs: {
            id: res.id,
            email: res.email,
            first_name: res.user_profile?.first_name,
            last_name: res.user_profile?.last_name,
          },
        });
      }

      yield eff.fork(
        login,
        {
          payload: {
            username: payload.email,
            password: payload.password,
          },
          redirectTo,
          updateProfile: {
            first_name: payload.first_name,
            last_name: payload.last_name,
          },
          callbacks,
        },
      );
    } catch (e) {
      callbacks?.setSubmitting?.(false);
      callbacks?.onError?.(e);
      if (e?.response?.data) {
        callbacks?.setErrors?.(e.response.data.error);
      }

      yield eff.call(throwError, e, reportChannel);
    }
  }

  function* loginWithToken({ accessToken }) {
    try {
      yield eff.call(
        api.setAccessToken,
        accessToken,
      );

      const user = yield eff.call(
        api.getProfile,
      );

      yield eff.call(
        api.setAdditionalParams,
        {
          userId: user.id,
        },
      );

      yield eff.put(
        reportChannel,
        {
          messageType: 'loginSuccess',
          message: {
            user,
            accessToken,
          },
        },
      );

      const createdAt = DateTime.fromISO(user.created_at)
      if (user.is_pro) {
        if (DateTime.utc().diff(createdAt, 'minutes').minutes > 30) return
        const lib = yield eff.call(api.getLibrary);
        if (lib[0].items.find(i => i.name === 'Program Templates').items.length > 1) return
        yield eff.fork(
          watchCheckLibrary,
          reportChannel,
        );
      }
    } catch (err) {
      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'error',
            body: 'Bad Access Token.',
          },
        },
      );
      yield eff.call(throwError, err);
    }
  }

  function* loginWithProvider({
    payload,
    meta: { provider },
  }) {
    try {
      const {
        user,
        access_token: accessToken,
      } = yield eff.call(
        api.connectWithProvider,
        {
          provider,
          body: payload,
          requestParams: {
            withCredentials: true,
          },
        },
      );
      yield eff.call(api.setAccessToken, accessToken);
      yield eff.call(api.setAdditionalParams, { userId: user.id });
      yield eff.put(
        reportChannel,
        {
          messageType: 'loginSuccess',
          message: {
            user,
            accessToken,
          },
        },
      );
    } catch (e) {
      yield eff.call(throwError, e, reportChannel);
    }
  }

  function* resetPassword({
    payload,
    callbacks,
  }) {
    try {
      yield eff.call(
        api.resetPassword,
        {
          body: {
            user: payload,
          },
        },
      );
      callbacks?.setSubmitting(false);
      callbacks?.setStatus('success');
    } catch (e) {
      callbacks?.setSubmitting(false);

      if (e?.response?.data) {
        callbacks?.setErrors?.(e.response.data.error);
      }
      yield eff.call(throwError, e);
    }
  }

  function* confirmPassword({
    payload,
    callbacks,
  }) {
    try {
      const response = yield eff.call(
        api.confirmPassword,
        {
          body: {
            user: payload,
          },
        },
      );

      callbacks?.setSubmitting?.(false);
      callbacks?.onSuccess?.(response);
    } catch (e) {
      callbacks?.setSubmitting?.(false);
      callbacks?.onError?.(e);
      if (e?.response?.data) {
        const { error } = e.response.data;
        callbacks?.setStatus?.(error);
        callbacks?.setErrors?.(error);
      }
      yield eff.call(throwError, e);
    }
  }

  function* confirmEmail({
    token,
    callbacks,
  }) {
    try {
      const response = yield eff.call(
        api.confirmEmail,
        {
          qs: {
            confirmation_token: token,
          },
        },
      );

      yield eff.put(
        reportChannel,
        {
          messageType: 'confirmEmailSuccess',
        },
      );

      callbacks?.onSuccess?.(response);
    } catch (e) {
      callbacks?.onError?.(e);
      yield eff.call(throwError, e);
    }
  }

  function* logout() {
    const isPro = yield eff.select(
      getProfile('is_pro'),
    );

    yield eff.put(setProfileState({
      isAuth: false,
    }));

    yield eff.put({
      type: 'GC_ANALYTICS',
      eventKey: isPro ? 'pro_logout' : 'client_logout',
    });

    yield eff.put(
      reportChannel,
      {
        messageType: 'logoutRequest',
      },
    );
  }

  function* profileSetup({
    payload,
    settings,
    callbacks,
  }) {
    try {
      const profile = yield eff.select(
        getProfile,
      );

      const userProfile = yield eff.call(
        api.updateEntity,
        {
          id: profile.user_profile.id,
          type: 'user_profiles',
          body: payload,
        },
      );
      const userSetting = yield eff.call(
        api.updateEntity,
        {
          id: profile.user_settings.id,
          type: 'user_settings',
          body: settings,
        },
      );

      yield eff.put(setProfileState({
        user_profile: userProfile,
        user_settings: userSetting,
      }));

      if (profile.is_pro && settings.is_completed_profile_customization) {
        yield eff.fork(
          watchCheckLibrary,
          reportChannel,
        );
      }

      callbacks?.setSubmitting?.(false);
      callbacks?.onSuccess?.(userProfile);
    } catch (e) {
      callbacks?.setSubmitting?.(false);
      callbacks?.onError?.(e);
      yield eff.call(throwError, e);
    }
  }

  function* checkEmail({
    email,
    callbacks,
  }) {
    try {
      const response = yield eff.call(
        api.getEmail,
        {
          qs: {
            email,
          },
        },
      );

      callbacks?.onSuccess(response);
    } catch (e) {
      callbacks?.onError(e);
      yield eff.call(throwError, e);
    }
  }

  function* stripeConnect({
    qs,
    callbacks,
  }) {
    try {
      const response = yield eff.call(
        api.stripeConnect,
        {
          qs,
        },
      );

      callbacks?.onSuccess?.(response);
    } catch (e) {
      callbacks?.onError?.(e);
      yield eff.put(
        reportChannel,
        {
          messageType: 'notify',
          message: {
            type: 'error',
            body: 'Failed to connect',
          },
        },
      );
      yield eff.call(throwError, e);
    }
  }

  function* createUser({
    data,
    callbacks,
  }) {
    yield eff.put({
      type: 'GC_ANALYTICS',
      eventKey: 'signup_submit',
      attrs: data,
    });

    try {
      yield eff.call(
        api.createUser,
        {
          body: data,
        },
      );

      const {
        user,
        access_token: accessToken,
      } = yield eff.call(
        api.authPassword,
        {
          body: {
            username: data.user.email,
            password: data.user.password,
            grant_type: 'password',
          },
        },
      );

      yield eff.call(api.setAccessToken, accessToken);
      yield eff.call(api.setAdditionalParams, { userId: user.id });

      yield eff.put(setProfileState({
        ...user,
        isAuth: true,
        isLoading: false,
      }));

      if (data.profile) {
        yield eff.call(
          api.updateEntity,
          {
            id: user.user_profile.id,
            type: 'user_profiles',
            body: data.profile,
          },
        );
      }

      yield eff.put({
        type: 'GC_ANALYTICS',
        eventKey: user.is_pro ? 'pro_signup' : 'client_signup',
        attrs: {
          id: user.id,
          email: user.email,
          first_name: data.profile?.first_name,
          last_name: data.profile?.last_name,
        },
      });

      callbacks?.onSuccess?.(user);
    } catch (e) {
      callbacks.onError?.(e);
      if (e?.response?.data) {
        callbacks?.setErrors?.(e.response.data.error);
      }
      yield eff.call(throwError, e);
    }
  }

  function* upgradeAccount({ endpoint, callbacks }) {
    const actions = createActionCreators('create', {
      resourceType: 'paymentIntents',
      requestKey: 'upgradeAccount',
    });

    yield eff.put(actions.pending());

    try {
      const invoice = yield eff.call(
        api.upgradeAccount,
        {
          endpoint,
        },
      );

      if (endpoint === 'upgrade') {
        callbacks?.onSuccess?.(invoice);
        return;
      }
      const intent = yield eff.call(getIntentTask, invoice.id);

      callbacks?.onSuccess?.(intent);

      yield eff.put({
        type: actionTypes.SET_PAYMENT_STATE,
        payload: {
          invoices: [invoice],
        },
      });

      yield eff.put(actions.succeeded({
        resources: [intent],
      }));
    } catch (e) {
      callbacks?.onError?.(e);

      yield eff.put(actions.failed({
        resources: [],
      }));

      yield eff.call(throwError, e);
    }
  }

  function* getUpgradeAccountPlan({ callbacks }) {
    const actions = createActionCreators('read', {
      resourceType: 'productPlans',
      requestKey: 'getUpgradeAccountPlan',
    });

    yield eff.put(actions.pending());

    try {
      const plan = yield eff.call(
        api.getUpgradeAccountPlan,
      );

      yield eff.put(actions.succeeded({
        resources: [plan],
      }));

      callbacks?.onSuccess?.(plan);
    } catch (e) {
      yield eff.put(actions.failed({
        resources: [],
      }));

      callbacks?.onError?.(e);

      yield eff.call(throwError, e);
    }
  }

  yield eff.takeEvery(actionTypes.SIGN_UP_REQUEST, signUp);
  yield eff.takeEvery(actionTypes.LOGIN_REQUEST, login);
  yield eff.takeEvery(actionTypes.LOGIN_WITH_TOKEN_REQUEST, loginWithToken);
  yield eff.takeEvery(actionTypes.LOGIN_WITH_PROVIDER_REQUEST, loginWithProvider);
  yield eff.takeEvery(actionTypes.RESET_PASSWORD_REQUEST, resetPassword);
  yield eff.takeEvery(actionTypes.CONFIRM_PASSWORD_REQUEST, confirmPassword);
  yield eff.takeEvery(actionTypes.REQUEST_CONFIRM_EMAIL, confirmEmail);
  yield eff.takeEvery(actionTypes.REQUEST_LOGOUT, logout);
  yield eff.takeEvery(actionTypes.PROFILE_SETUP_REQUEST, profileSetup);
  yield eff.takeEvery(actionTypes.REQUEST_CHECK_EMAIL, checkEmail);
  yield eff.takeEvery(actionTypes.REQUEST_CREATE_USER, createUser);
  yield eff.takeEvery(actionTypes.REQUEST_UPGRADE_ACCOUNT, upgradeAccount);
  yield eff.takeEvery(actionTypes.REQUEST_GET_UPGRADE_ACCOUNT_PLAN, getUpgradeAccountPlan);
  yield eff.takeEvery(actionTypes.REQUEST_CONNECT_STRIPE, stripeConnect);
}
